import { useState, type FormEvent } from 'react';
import type { CaseRole } from '@casetrace/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useInviteMember } from './use-case-members';

type InviteFormProps = {
  readonly caseId: string;
};

const ROLE_OPTIONS: { readonly value: CaseRole; readonly label: string }[] = [
  { value: 'member', label: 'Medlem' },
  { value: 'viewer', label: 'Leser' },
  { value: 'owner', label: 'Eier' },
];

export function InviteForm({ caseId }: InviteFormProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CaseRole>('member');
  const inviteMember = useInviteMember(caseId);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim()) return;
    await inviteMember.mutateAsync({ email: email.trim(), role });
    setEmail('');
    setRole('member');
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-white/[0.07] bg-white/[0.03] p-3"
    >
      <div className="flex flex-col gap-3">
        <Input
          id="invite-email"
          label="Inviter med e-post"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="navn@firma.no"
        />
        <label className="flex flex-col gap-1.5 text-xs font-medium text-[#8b8d9a]">
          Rolle
          <select
            value={role}
            onChange={(event) => setRole(event.target.value as CaseRole)}
            className="rounded-md border border-white/[0.1] bg-[#1a1b23] px-3 py-2 text-sm text-[#ededf0] outline-none focus:border-accent/60"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {inviteMember.isError && (
        <p className="mt-3 text-xs text-danger/80">Kunne ikke sende invitasjonen.</p>
      )}

      <Button
        className="mt-3 w-full"
        type="submit"
        disabled={!email.trim() || inviteMember.isPending}
      >
        {inviteMember.isPending ? 'Sender...' : 'Send invitasjon'}
      </Button>
    </form>
  );
}
